import React, { useState, useMemo } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";

import { FormikProps } from "formik";
import { Autocomplete, TextField } from "@mui/material";
import CustomSelectUpdated from "../../../components/Dashboard/Talent/Card/CustomSelected_updated";
import { StageCardFormValues } from "@/types/Card";
import { SearchTagSelector } from "../../../components/Dashboard/Talent/Card/Card_Creation/SearchTagSelector_updated";
import CustomTextField from "../../../components/common/CustomTextField";
import CardTextField from "../../../components/Dashboard/Talent/Card/CardTextField";
import { useGetCategories } from "../../../services/api/card.api";
import { useErrorResponse } from "../../../hooks/useErrorResponse";

const venue_sizes = [
  { label: "Small", value: "Small" },
  { label: "Medium", value: "Medium" },
  { label: "Large", value: "Large" },
  { label: "Broadcast", value: "Broadcast" },
];

interface StepOneProps {
  formik: FormikProps<StageCardFormValues>;
  onNext: () => void;
}

const LabelBlock = ({
  label,
  required = false,
  helper,
}: {
  label: string;
  required?: boolean;
  helper?: string;
}) => (
  <Box>
    <Typography
      sx={{ font: "var(--semibold-md)", color: "var(--neutral-1000)", mb: 0.5 }}
    >
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
    {helper && (
      <Typography sx={{ font: "var(--body-sm)", color: "var(--neutral-500)" }}>
        {helper}
      </Typography>
    )}
  </Box>
);

const StepOne_StageInfo = ({ formik, onNext }: StepOneProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedCategory = formik.values.category;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: categoryData, error: categoryError } = useGetCategories({
    key: ["categories"],
  });
  useErrorResponse(categoryError);

  const categoryOptions =
    categoryData?.data?.map((category) => ({
      label: category.name,
      value: category.name,
    })) || [];

  const matchedCategory = useMemo(() => {
    return categoryData?.data?.find(
      (opt) => opt.name.toLowerCase() === selectedCategory?.toLowerCase()
    );
  }, [selectedCategory, categoryData]);

  const isCategoryExisting = !!matchedCategory;

  const currentSubcategories = useMemo(() => {
    return (
      matchedCategory?.sub_categories.map((sub) => ({
        label: sub.name,
        value: sub.name,
      })) || []
    );
  }, [matchedCategory]);

  const handleNext = async () => {
    setIsSubmitting(true);
    const errors = await formik.validateForm();

    const fields = ["title", "venue_size"];
    const stepErrors = Object.keys(errors).filter((key) =>
      fields.includes(key)
    );

    if (stepErrors.length === 0) {
      onNext();
    } else {
      const touchedFields = fields.reduce(
        (acc, field) => {
          acc[field] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      formik.setTouched(touchedFields, true);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="start"
        fontFamily="Lexend"
        sx={{ color: "#333", pt: 3, pb: 2 }}
      >
        🎤 Stage Info
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Card Title"
            required
            helper="Add a descriptive name for your StageCard"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Enter card title"
            {...formik.getFieldProps("title")}
            error={!!formik.touched.title && !!formik.errors.title}
            helperText={formik.touched.title && formik.errors.title}
          />
        </Grid>
        <>
          <Grid item xs={12} md={6}>
            <LabelBlock
              label="Banner Title"
              helper="Make your banner title catchy and unique for your public event."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              {...formik.getFieldProps("banner_title")}
              errorMessage={
                formik.touched.banner_title && formik.errors.banner_title
              }
            />
          </Grid>
        </>
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Category"
            helper="Choose the category and sub-category most suitable for your Card"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo
            options={categoryOptions.map((opt) => opt.label)}
            {...formik.getFieldProps("category")}
            onChange={(_, value) => {
              formik.setFieldValue("category", value || "");
              formik.setFieldValue("sub_category", ""); // reset subcategory
            }}
            onInputChange={(_, value) => {
              formik.setFieldValue("category", value);
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                placeholder=""
                variant="outlined"
                fullWidth
                errorMessage={formik.touched.category && formik.errors.category}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LabelBlock label="Subcategory" />
        </Grid>

        <Grid item xs={12} md={6}>
          {isCategoryExisting ? (
            <CustomSelectUpdated
              value={formik.values.sub_category}
              onChange={(e) =>
                formik.setFieldValue("sub_category", e.target.value)
              }
              options={currentSubcategories}
              placehoder=""
            />
          ) : (
            <TextField
              label=""
              placeholder=""
              value={formik.values.sub_category}
              onChange={(e) =>
                formik.setFieldValue("sub_category", e.target.value)
              }
              fullWidth
              variant="outlined"
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Search tags"
            helper="Tag your Card with buzz words that are relevant to the services you offer. you can use maxium 3 tags.(Optional)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SearchTagSelector
            selectedTags={formik.values.tags || []}
            setFieldValue={formik.setFieldValue}
            name="tags"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Brief Description"
            helper="Insert a brief description of your card."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            {...formik.getFieldProps("short_description")}
            errorMessage={
              formik.touched.short_description && formik.errors.short_description
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LabelBlock
              label="Detailed Description"
              helper="Summarize the services on this card, highlighting your unique talents and the value you offer to clients."
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <CardTextField
            multiline
            rows={4}
            {...formik.getFieldProps("description")}
            errorMessage={
              formik.touched.description && formik.errors.description
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Venue Size"
            required
            helper="Choose the appropriate venue size for the audience"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomSelectUpdated
            value={formik.values.venue_size}
            onChange={(e) => formik.setFieldValue("venue_size", e.target.value)}
            options={venue_sizes}
            placehoder="Select venue size"
            error={!!formik.touched.venue_size && !!formik.errors.venue_size}
            helperText={formik.touched.venue_size && formik.errors.venue_size}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSubmitting}
              sx={{
                fontWeight: 600,
                background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                px: 4,
                py: 1.5,
                minWidth: 140,
                "&:hover": {
                  boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
                },
              }}
            >
              Next
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default StepOne_StageInfo;
